import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { CmsHeroFallback } from "@gaole/shared";
import { CMS_HERO_FALLBACK_DEFAULTS } from "@gaole/shared";
import { api } from "../lib/api";
import { useAuth } from "../stores/auth";
import { PageLoader } from "./ui";
import { ProductHeroImage } from "./ProductCard";
import { SwipeCarousel } from "./storefront/SwipeCarousel";

interface HeroSlide {
  id: string;
  imageUrl: string;
  title: string | null;
  subtitle: string | null;
  href: string | null;
  external: boolean;
}

function HeroHeadline({ text }: { text: string }) {
  const highlight = "collectibles";
  const idx = text.toLowerCase().indexOf(highlight);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="text-gradient">{text.slice(idx, idx + highlight.length)}</span>
      {text.slice(idx + highlight.length)}
    </>
  );
}

function StaticHeroFallback({ fallback }: { fallback: CmsHeroFallback }) {
  const { moduleEnabled } = useAuth();
  return (
    <section className="neon-ring relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-pink/30 via-surface to-brand-blue/30 p-6 lg:p-8">
      <img src="/logo-mark.png" alt="" className="absolute -right-6 -top-6 h-36 w-36 opacity-15 lg:h-48 lg:w-48" />
      <p className="font-display text-[10px] uppercase tracking-[0.3em] text-brand-pink lg:text-xs">
        {fallback.eyebrow}
      </p>
      <h1 className="mt-2 font-display text-2xl font-bold leading-tight lg:text-3xl">
        <HeroHeadline text={fallback.headline} />
      </h1>
      <p className="mt-2 max-w-sm text-xs text-white/60 lg:text-sm">{fallback.description}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {moduleEnabled("packs") && (
          <Link to="/packs" className="rounded-xl bg-gradient-brand px-4 py-2.5 text-xs font-semibold glow-pink lg:text-sm">
            Open a pack
          </Link>
        )}
        {moduleEnabled("ecommerce") && (
          <Link to="/shop" className="rounded-xl bg-white/10 px-4 py-2.5 text-xs font-semibold lg:text-sm">
            Browse shop
          </Link>
        )}
      </div>
    </section>
  );
}

function SlideContent({ slide }: { slide: HeroSlide }) {
  const inner = (
    <div className="relative overflow-hidden rounded-3xl">
      <ProductHeroImage
        src={slide.imageUrl}
        alt={slide.title ?? ""}
        aspectClassName="aspect-video w-full"
        overlay={slide.title || slide.subtitle ? "caption" : false}
        blurClassName="scale-105 blur-xl opacity-40 saturate-125"
        imageClassName="object-cover"
      />
      {(slide.title || slide.subtitle) && (
        <div className="absolute bottom-0 left-0 right-0 z-30 p-5 lg:p-8 drop-shadow-[0_2px_10px_rgba(0,0,0,0.95)] [text-shadow:0_1px_2px_rgba(0,0,0,0.85)]">
          {slide.title ? <h2 className="font-display text-lg font-bold lg:text-2xl">{slide.title}</h2> : null}
          {slide.subtitle ? <p className="mt-1 text-xs text-white/90 lg:text-sm">{slide.subtitle}</p> : null}
          {slide.href ? (
            <span className="mt-3 inline-block rounded-full bg-gradient-brand px-4 py-1.5 text-[11px] font-semibold">
              Explore →
            </span>
          ) : null}
        </div>
      )}
    </div>
  );

  if (!slide.href) return inner;
  if (slide.external) {
    return (
      <a href={slide.href} target="_blank" rel="noreferrer" className="block">
        {inner}
      </a>
    );
  }
  return (
    <Link to={slide.href} className="block">
      {inner}
    </Link>
  );
}

export function HeroCarousel() {
  const { moduleEnabled } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["content-hero"],
    queryFn: () => api<{ slides: HeroSlide[]; fallback: CmsHeroFallback }>("/content/hero"),
  });

  const fallback = data?.fallback ?? CMS_HERO_FALLBACK_DEFAULTS;
  const slides = moduleEnabled("cms") ? (data?.slides ?? []) : [];

  if (isLoading) return <PageLoader />;
  if (!slides.length) return <StaticHeroFallback fallback={fallback} />;

  if (slides.length === 1) {
    return <SlideContent slide={slides[0]!} />;
  }

  return (
    <SwipeCarousel autoplayMs={6000} showDots showProgress loop>
      {slides.map((slide) => (
        <SlideContent key={slide.id} slide={slide} />
      ))}
    </SwipeCarousel>
  );
}
